import { ChangeEvent, FC, useRef, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { MenuItem, Popover, Select } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import { DateRangePresetKey, DateRangeValue } from '../../utils/interface';
import {
  DATE_INPUT_FORMAT,
  DATE_RANGE_PRESETS,
  formatDateInput,
  formatRangeLabel,
  getPresetRange,
  parseDateInput,
} from '../../utils/attendanceDateRange';
import RangeDay from './RangeDay';
import './DateRangeFilter.scss';

interface DateRangeFilterProps {
  preset: DateRangePresetKey;
  range: DateRangeValue;
  onChange: (preset: DateRangePresetKey, range: DateRangeValue) => void;
}

const INVALID_DATE_MESSAGE = `Enter a valid past or current date (${DATE_INPUT_FORMAT})`;
const INVALID_ORDER_MESSAGE = 'End date must be on or after the start date';

const DateRangeFilter: FC<DateRangeFilterProps> = ({ preset, range, onChange }) => {
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState<boolean>(false);
  const [pendingStart, setPendingStart] = useState<Dayjs | null>(null);
  const [pendingEnd, setPendingEnd] = useState<Dayjs | null>(null);
  // Typed text is kept separately so partially typed dates are not overwritten while typing.
  const [startText, setStartText] = useState<string>('');
  const [endText, setEndText] = useState<string>('');

  const setPendingRange = (start: Dayjs | null, end: Dayjs | null) => {
    setPendingStart(start);
    setPendingEnd(end);
    setStartText(start ? formatDateInput(start) : '');
    setEndText(end ? formatDateInput(end) : '');
  };

  const handleSelectPreset = (key: DateRangePresetKey) => {
    if (key === 'custom') {
      const isCustom = preset === 'custom';
      setPendingRange(isCustom ? range.start : null, isCustom ? range.end : null);
      setIsCalendarOpen(true);
      return;
    }
    onChange(key, getPresetRange(key));
  };

  const handleDayClick = (day: Dayjs) => {
    // First click (or a click after a complete range) starts a new range; second click completes it.
    if (!pendingStart || pendingEnd) {
      setPendingRange(day, null);
      return;
    }
    if (day.isBefore(pendingStart, 'day')) {
      setPendingRange(day, pendingStart);
    } else {
      setPendingRange(pendingStart, day);
    }
  };

  const handleStartTextChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setStartText(value);
    setPendingStart(parseDateInput(value));
  };

  const handleEndTextChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setEndText(value);
    setPendingEnd(parseDateInput(value));
  };

  const startError = startText.trim() !== '' && !parseDateInput(startText) ? INVALID_DATE_MESSAGE : '';
  const endError = endText.trim() !== '' && !parseDateInput(endText) ? INVALID_DATE_MESSAGE : '';
  const orderError =
    pendingStart && pendingEnd && pendingEnd.isBefore(pendingStart, 'day') ? INVALID_ORDER_MESSAGE : '';
  const errorMessage = startError || endError || orderError;
  const canApply = !!pendingStart && !errorMessage;

  const handleApply = () => {
    if (!pendingStart || !canApply) return;
    onChange('custom', { start: pendingStart.startOf('day'), end: (pendingEnd ?? pendingStart).startOf('day') });
    setIsCalendarOpen(false);
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') handleApply();
  };

  const handleClose = () => setIsCalendarOpen(false);

  const renderSelectedValue = (key: DateRangePresetKey) =>
    key === 'custom' ? formatRangeLabel(range) : DATE_RANGE_PRESETS.find((item) => item.key === key)?.label;

  return (
    <div className='dateRangeFilter' ref={anchorRef}>
      <Select
        value={preset}
        size='small'
        className='dateRangeSelect'
        renderValue={renderSelectedValue}
        inputProps={{ 'aria-label': 'Filter by date' }}
        MenuProps={{ classes: { paper: 'dateRangeMenuPaper' } }}
      >
        {DATE_RANGE_PRESETS.map(({ key, label }) => (
          <MenuItem key={key} value={key} onClick={() => handleSelectPreset(key)}>
            {label}
          </MenuItem>
        ))}
      </Select>
      <Popover
        open={isCalendarOpen}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <div className='dateRangeCalendarPopover'>
          <div className='dateRangeInputs'>
            <label className='dateRangeInputLabel'>
              From
              <input
                type='text'
                inputMode='numeric'
                className={`dateRangeInput ${startError || orderError ? 'invalid' : ''}`}
                placeholder={DATE_INPUT_FORMAT}
                maxLength={10}
                value={startText}
                aria-invalid={!!(startError || orderError)}
                onChange={handleStartTextChange}
                onKeyDown={handleInputKeyDown}
              />
            </label>
            <label className='dateRangeInputLabel'>
              To
              <input
                type='text'
                inputMode='numeric'
                className={`dateRangeInput ${endError || orderError ? 'invalid' : ''}`}
                placeholder={DATE_INPUT_FORMAT}
                maxLength={10}
                value={endText}
                aria-invalid={!!(endError || orderError)}
                onChange={handleEndTextChange}
                onKeyDown={handleInputKeyDown}
              />
            </label>
          </div>
          {errorMessage && <div className='dateRangeError' role='alert'>{errorMessage}</div>}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateCalendar
              value={null}
              className='dateRangeCalendar'
              disableFuture
              maxDate={dayjs()}
              onChange={handleDayClick}
              slots={{ day: RangeDay }}
              slotProps={{
                day: { rangeStart: pendingStart, rangeEnd: pendingEnd } as unknown as Partial<PickersDayProps<Dayjs>>,
              }}
            />
          </LocalizationProvider>
          <div className='dateRangeActions'>
            <button type='button' className='dateRangeCancel' onClick={handleClose}>Cancel</button>
            <button type='button' className='dateRangeApply' disabled={!canApply} onClick={handleApply}>Apply</button>
          </div>
        </div>
      </Popover>
    </div>
  );
};

export default DateRangeFilter;
